import { useEffect, useMemo, useState, useRef, Fragment, memo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { slugify } from "@/lib/utils";
import { toast as toastUI } from "@/components/ui/sonner";
import { GitBranch, PlusCircle, Search, Pencil, Trash2, X, Maximize2, Heading1, Heading2, Heading3, Bold, List, Eye, Link2 } from "lucide-react";
import { MDXEditor, headingsPlugin, listsPlugin, markdownShortcutPlugin, toolbarPlugin, BoldItalicUnderlineToggles, ListsToggle, BlockTypeSelect, Button as MDXToolbarButton, Separator, quotePlugin, CodeToggle, InsertTable, tablePlugin, linkPlugin, linkDialogPlugin, CreateLink, InsertAdmonition, directivesPlugin, AdmonitionDirectiveDescriptor, codeBlockPlugin, InsertThematicBreak, thematicBreakPlugin } from "@mdxeditor/editor";
// removed unused createPortal
import "@mdxeditor/editor/style.css";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useChatwootToken } from "@/hooks/use-chatwoot-token";
import { getChatwootSDK } from "@/integrations/chatwoot/sdk";
import { useNavigate } from "react-router-dom";

interface AgentDefault {
  id: string;
  slug: string;
  nome: string;
  system_prompt: string | null;
  activation_condition_prompt: string | null;
  base_agent_type: string | null;
  model: string | null;
  temperature: number | null;
  max_tokens: number | null;
  status: string;
  version: number;
}

interface Agent {
  id: string;
  tenant_id: string;
  default_id: string | null;
  default_version: number | null;
  slug: string;
  nome: string;
  system_prompt: string | null;
  activation_condition_prompt: string | null;
  prompt_finishing?: string | null;
  base_agent_type: string | null;
  model: string | null;
  temperature: number | null;
  max_tokens: number | null;
  status: string;
  overrides: any;
  deployment_stage: string;
  invoke_limit: number | null;
  invoke_used: number;
  parent_agent_id: string | null;
  version: number;
  is_current: boolean;
}

const applyFormat = (textarea: HTMLTextAreaElement, type: 'h1'|'h2'|'h3'|'bold'|'list') => {
  const start = textarea.selectionStart ?? 0;
  const end = textarea.selectionEnd ?? 0;
  const text = textarea.value;
  const selected = text.slice(start, end);
  let replacement = selected;
  if (type === 'bold') {
    replacement = `**${selected || 'texto'}**`;
    const next = text.slice(0, start) + replacement + text.slice(end);
    textarea.value = next;
    textarea.setSelectionRange(start + 2, start + 2 + (selected || 'texto').length);
  } else if (type === 'list') {
    const lines = (selected || '').split(/\r?\n/);
    const formatted = lines.map(l => l ? `- ${l}` : '- ').join('\n');
    replacement = formatted || '- ';
    const next = text.slice(0, start) + replacement + text.slice(end);
    textarea.value = next;
    textarea.setSelectionRange(start, start + replacement.length);
  } else {
    const prefix = type === 'h1' ? '# ' : type === 'h2' ? '## ' : '### ';
    replacement = selected ? `${prefix}${selected}` : `${prefix}`;
    const next = text.slice(0, start) + replacement + text.slice(end);
    textarea.value = next;
    textarea.setSelectionRange(start + prefix.length, start + replacement.length);
  }
  const event = new Event('input', { bubbles: true });
  textarea.dispatchEvent(event);
};

const escapeHtml = (s: string) => s.replace(/[&<>"]+/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c] as string));
const parseMarkdownSubset = (md: string) => {
  const lines = md.split(/\r?\n/);
  let html = "";
  let inList = false;
  const closeList = () => { if (inList) { html += "</ul>"; inList = false; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^\s*-\s+/.test(line)) {
      if (!inList) { html += "<ul class=\"list-disc pl-6 my-2\" style=\"list-style-type:disc; padding-left:1.5rem; margin:0.5rem 0;\">"; inList = true; }
      const item = escapeHtml(line.replace(/^\s*-\s+/, ""));
      html += `<li>${item}</li>`;
      continue;
    }
    closeList();
    if (line.startsWith("### ")) { html += `<h3 class=\"text-lg md:text-xl font-semibold leading-tight\" style=\"font-size:1.125rem; font-weight:600; line-height:1.4;\">${escapeHtml(line.slice(4))}</h3>`; continue; }
    if (line.startsWith("## ")) { html += `<h2 class=\"text-xl md:text-2xl font-semibold leading-tight\" style=\"font-size:1.25rem; font-weight:600; line-height:1.4;\">${escapeHtml(line.slice(3))}</h2>`; continue; }
    if (line.startsWith("# ")) { html += `<h1 class=\"text-2xl md:text-3xl font-bold leading-tight\" style=\"font-size:1.5rem; font-weight:700; line-height:1.4;\">${escapeHtml(line.slice(2))}</h1>`; continue; }
    const bolded = escapeHtml(line).replace(/\*\*([^*]+)\*\*/g, '<strong class="font-bold" style="font-weight:700">$1</strong>');
    html += bolded ? `<p class=\"my-1\">${bolded}</p>` : "<br/>";
  }
  closeList();
  return html;
};

const htmlToMarkdownSubset = (html: string) => {
  const container = document.createElement('div');
  container.innerHTML = html;
  const lines: string[] = [];
  const walk = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.tagName === 'H1') { lines.push(`# ${el.textContent || ''}`); return; }
      if (el.tagName === 'H2') { lines.push(`## ${el.textContent || ''}`); return; }
      if (el.tagName === 'H3') { lines.push(`### ${el.textContent || ''}`); return; }
      if (el.tagName === 'UL') {
        Array.from(el.children).forEach((li) => {
          const text = (li as HTMLElement).innerText || '';
          lines.push(`- ${text}`);
        });
        return;
      }
      if (el.tagName === 'P') {
        const pMd = (el.innerHTML || '')
          .replace(/<(strong|b)[^>]*>(.*?)<\/(strong|b)>/g, '**$2**')
          .replace(/<br\s*\/>/g, '\n');
        const tmp = document.createElement('div'); tmp.innerHTML = pMd; lines.push(tmp.textContent || '');
        return;
      }
      if (el.tagName === 'BR') { lines.push(''); return; }
    }
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent || '').trim();
      if (text) lines.push(text);
    }
    let child = node.firstChild;
    while (child) { const next = child.nextSibling; walk(child); child = next; }
  };
  walk(container);
  return lines.join('\n');
};

const MarkdownEditor = memo(({ label, value, onChange, placeholder, minHeight = 160, onMaximize }: { label?: string; value: string; onChange: (v: string)=>void; placeholder?: string; minHeight?: number; onMaximize?: (title: string, value: string, onChange: (v: string)=>void) => void }) => {
  const [localValue, setLocalValue] = useState(value || "");
  const [visualMode, setVisualMode] = useState(true);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const visualRef = useRef<HTMLDivElement | null>(null);
  const visualInputFlag = useRef<boolean>(false);
  useEffect(() => {
    if (typeof value === 'string' && value !== localValue) {
      setLocalValue(value);
    }
  }, [value]);
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => { setLocalValue(e.target.value); onChange(e.target.value); };

  const sanitizeAllowedHtml = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const walk = (node: Node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const allowed = ['H1','H2','H3','STRONG','B','UL','LI','P','BR','EM','SPAN','DIV'];
        if (!allowed.includes(el.tagName)) {
          const p = document.createElement('p');
          while (el.firstChild) p.appendChild(el.firstChild);
          el.replaceWith(p);
        }
      }
      let child = node.firstChild;
      while (child) { const next = child.nextSibling; walk(child); child = next; }
    };
    walk(div);
    return div.innerHTML;
  };

  const handleVisualInput = (e: React.FormEvent<HTMLDivElement>) => {
    const html = sanitizeAllowedHtml((e.target as HTMLDivElement).innerHTML);
    const md = htmlToMarkdownSubset(html);
    visualInputFlag.current = true;
    setLocalValue(md);
    onChange(md);
  };

  useEffect(() => {
    if (visualMode && visualRef.current) {
      if (!visualInputFlag.current) {
        visualRef.current.innerHTML = parseMarkdownSubset(localValue);
      }
      visualInputFlag.current = false;
    }
  }, [visualMode, localValue]);

  const execInDocument = (cmd: string, value?: string) => {
    visualRef.current?.focus();
    document.execCommand(cmd, false, value);
  };

  const captureSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      visualInputFlag.current = false;
      return r;
    }
    return null;
  };

  const syncFromVisual = () => {
    if (!visualRef.current) return;
    const html = sanitizeAllowedHtml(visualRef.current.innerHTML);
    const md = htmlToMarkdownSubset(html);
    visualInputFlag.current = true;
    setLocalValue(md);
    onChange(md);
  };

  const wrapSelectionTag = (tagName: 'H1'|'H2'|'H3'|'STRONG') => {
    const range = captureSelection();
    if (!range || !visualRef.current) return;
    if (!visualRef.current.contains(range.commonAncestorContainer)) return;
    const selectedText = range.toString();
    const styleMap: Record<string, string> = {
      H1: 'font-size:1.5rem; font-weight:700; line-height:1.4; margin:0.25rem 0;',
      H2: 'font-size:1.25rem; font-weight:600; line-height:1.4; margin:0.25rem 0;',
      H3: 'font-size:1.125rem; font-weight:600; line-height:1.4; margin:0.25rem 0;',
      STRONG: 'font-weight:700;'
    };
    if (selectedText) {
      const el = document.createElement(tagName);
      el.setAttribute('style', styleMap[tagName]);
      el.textContent = selectedText;
      range.deleteContents();
      range.insertNode(el);
    } else {
      // Se não há seleção, aplicar no bloco atual
      const sel = window.getSelection();
      const anchor = sel?.anchorNode || range.commonAncestorContainer;
      let block = anchor as HTMLElement;
      if (block.nodeType !== Node.ELEMENT_NODE) {
        block = (block.parentElement || visualRef.current);
      }
      // Subir até um bloco dentro do editor
      while (block && block !== visualRef.current && !['P','DIV','H1','H2','H3','LI'].includes(block.tagName)) {
        block = block.parentElement as HTMLElement;
      }
      if (!block || block === visualRef.current) return;
      const newEl = document.createElement(tagName);
      newEl.setAttribute('style', styleMap[tagName]);
      newEl.textContent = block.textContent || '';
      block.replaceWith(newEl);
    }
    const sel = window.getSelection();
    sel?.removeAllRanges();
    const newRange = document.createRange();
    const target = visualRef.current.querySelector(tagName) || visualRef.current;
    newRange.selectNodeContents(target);
    sel?.addRange(newRange);
    syncFromVisual();
  };

  const applyListToSelection = () => {
    const range = captureSelection();
    if (!range || !visualRef.current) return;
    if (!visualRef.current.contains(range.commonAncestorContainer)) return;
    const selectedText = range.toString();
    const lines = (selectedText ? selectedText : (range.commonAncestorContainer.textContent || '').trim()).split(/\r?\n/).filter(Boolean);
    const ul = document.createElement('UL');
    ul.setAttribute('style','list-style-type:disc; padding-left:1.5rem; margin:0.5rem 0;');
    lines.forEach((ln) => {
      const li = document.createElement('LI');
      li.textContent = ln.trim();
      ul.appendChild(li);
    });
    range.deleteContents();
    range.insertNode(ul);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNode(ul);
    sel?.addRange(newRange);
    syncFromVisual();
  };

  const applyVisual = (type: 'h1'|'h2'|'h3'|'bold'|'list') => {
    if (!visualMode || !visualRef.current) return;
    const sel = captureSelection();
    if (sel) {
      const s = window.getSelection();
      s?.removeAllRanges();
      s?.addRange(sel);
    }
    if (type === 'h1') wrapSelectionTag('H1');
    else if (type === 'h2') wrapSelectionTag('H2');
    else if (type === 'h3') wrapSelectionTag('H3');
    else if (type === 'bold') wrapSelectionTag('STRONG');
    else applyListToSelection();
  };

  return (
      <div className="space-y-2">
        {label && <label className="text-sm">{label}</label>}
      <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onMouseDown={(e) => e.preventDefault()} onClick={() => visualMode ? applyVisual('h1') : (ref.current && applyFormat(ref.current, 'h1'))}><Heading1 className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onMouseDown={(e) => e.preventDefault()} onClick={() => visualMode ? applyVisual('h2') : (ref.current && applyFormat(ref.current, 'h2'))}><Heading2 className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onMouseDown={(e) => e.preventDefault()} onClick={() => visualMode ? applyVisual('h3') : (ref.current && applyFormat(ref.current, 'h3'))}><Heading3 className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onMouseDown={(e) => e.preventDefault()} onClick={() => visualMode ? applyVisual('bold') : (ref.current && applyFormat(ref.current, 'bold'))}><Bold className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onMouseDown={(e) => e.preventDefault()} onClick={() => visualMode ? applyVisual('list') : (ref.current && applyFormat(ref.current, 'list'))}><List className="h-4 w-4" /></Button>
          <Button variant={visualMode?"default":"outline"} size="sm" onClick={() => setVisualMode(!visualMode)}><Eye className="h-4 w-4" /></Button>
        </div>
        <Button variant="ghost" size="icon" onClick={() => onMaximize && onMaximize(label || 'Editor', localValue, onChange)}><Maximize2 className="h-4 w-4" /></Button>
      </div>
      {visualMode ? (
        <div
          ref={visualRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleVisualInput}
          onKeyUp={handleVisualInput}
          onPaste={(e) => {
            setTimeout(() => handleVisualInput(e as any), 0);
          }}
          className="rounded border p-3 text-sm"
          style={{ minHeight: `${minHeight}px`, lineHeight: 1.5, fontSize: '1rem' }}
        />
      ) : (
        <Textarea
          ref={ref}
          placeholder={placeholder}
          value={localValue}
          onChange={handleChange}
          style={{ minHeight: `${minHeight}px` }}
          className="focus-visible:ring-0 focus-visible:ring-offset-0 focus:ring-0 focus:outline-none outline-none"
        />
      )}
    </div>
  );
});

export default function IAgents() {
  const { tenantId, isAdminSaas } = useTenant();
  const [defaults, setDefaults] = useState<AgentDefault[]>([]);
  const [items, setItems] = useState<Agent[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Agent | null>(null);
  const [form, setForm] = useState<Partial<Agent & { default_slug?: string }>>({ status: "ativo", deployment_stage: "teste", version: 1, is_current: false });
  const [tenantSelectList, setTenantSelectList] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const activeTenantId = selectedTenantId || tenantId || null;
  const canManage = useMemo(() => !!activeTenantId || isAdminSaas, [activeTenantId, isAdminSaas]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [confirmVersionOpen, setConfirmVersionOpen] = useState(false);
  const [versionBase, setVersionBase] = useState<Agent | null>(null);
  const [prepareVersionOnly, setPrepareVersionOnly] = useState(false);
  const [confirmActivateOpen, setConfirmActivateOpen] = useState(false);
  const [activateTarget, setActivateTarget] = useState<Agent | null>(null);
  const [activateMode, setActivateMode] = useState<'definitivo'|'limitado'>('definitivo');
  const [activateLimit, setActivateLimit] = useState<number>(10);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Agent | null>(null);
  const [limitEditOpen, setLimitEditOpen] = useState(false);
  const [limitEditTarget, setLimitEditTarget] = useState<Agent | null>(null);
  const [limitEditValue, setLimitEditValue] = useState<string>("");
  const [limitUnlimited, setLimitUnlimited] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [fullscreenEditor, setFullscreenEditor] = useState<{ title: string; value: string; onChange: (v: string)=>void } | null>(null);
  const [fsPreview, setFsPreview] = useState(true);
  const [kbList, setKbList] = useState<Array<{ id: string; nome: string; descricao?: string | null }>>([]);
  const [selectedKbIds, setSelectedKbIds] = useState<string[]>([]);
  const { hasToken } = useChatwootToken();
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxes, setInboxes] = useState<Array<{ id: number; name: string; channel_type?: string }>>([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const navigate = useNavigate();
  const [cwToken, setCwToken] = useState<string | null>(null);
  const [cwAccountId, setCwAccountId] = useState<number | null>(null);
  const [installedHooks, setInstalledHooks] = useState<Record<number, number>>({});
  const [hookLoadingId, setHookLoadingId] = useState<number | null>(null);
  const [globalHookId, setGlobalHookId] = useState<number | null>(null);
  const endpointUrl = 'https://n8n.servinder.com.br/webhook/mensagens';

  const extractArray = (upstream: any) => {
    if (Array.isArray(upstream)) return upstream;
    const candidates = [
      upstream?.payload,
      upstream?.data,
      upstream?.data?.payload,
      upstream?.data?.data,
      upstream?.result,
      upstream?.body,
      upstream?.payload?.webhooks,
      upstream?.webhooks,
      upstream?.data?.payload?.webhooks,
      upstream?.data?.payload?.inboxes,
    ];
    for (const c of candidates) { if (Array.isArray(c)) return c; }
    return [] as any[];
  };

  const normalizeUrl = (u: string) => u ? u.trim().replace(/[`"\s]+/g, '').replace(/\/+$/, '').toLowerCase() : '';
  const normEndpoint = normalizeUrl(endpointUrl);

  const openInboxDialog = async () => {
    const tenantForLookup = activeTenantId || tenantId;
    if (!tenantForLookup) return navigate('/configuracoes/chatwoot?tab=setup&sub=agents');
    setInboxOpen(true);
    setInboxLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');
      const { data: profile } = await supabase.from('profiles' as any).select('chatwoot_user_token').eq('id', user.id).maybeSingle();
      const { data: tenant } = await supabase.from('tenants' as any).select('chatwoot_account_id').eq('id', tenantForLookup).maybeSingle();
      const token = (profile as any)?.chatwoot_user_token;
      const accountId = (tenant as any)?.chatwoot_account_id;
      if (!token || !accountId) throw new Error('Chatwoot não configurado para este tenant');
      setCwToken(token);
      setCwAccountId(Number(accountId));
      const sdk = getChatwootSDK();
      const upstream: any = await sdk.appProxy({ path: `/api/v1/accounts/${accountId}/inboxes`, method: 'GET', token });
      const arr = extractArray(upstream);
      const list = arr.map((x: any) => ({ id: Number(x.id), name: String(x.name || x.channel?.name || 'Inbox'), channel_type: x?.channel_type || x?.channel_type_name || x?.channel }));
      setInboxes(list);
      // buscar webhooks existentes do account
      try {
        const hooksUpstream: any = await sdk.appProxy({ path: `/api/v1/accounts/${accountId}/webhooks`, method: 'GET', token });
        const hooksArr = extractArray(hooksUpstream);
        const map: Record<number, number> = {};
        let globalId: number | null = null;
        hooksArr.forEach((h: any) => {
          const url = normalizeUrl(String(h?.url || ''));
          const inboxId = Number(h?.inbox_id ?? h?.inbox?.id ?? 0);
          const id = Number(h?.id || 0);
          if (url === normEndpoint && id) {
            if (inboxId) map[inboxId] = id; else globalId = id;
          }
        });
        if (globalId) {
          // marcar todas as inboxes como instaladas para refletir imediatamente na UI
          const mapAll: Record<number, number> = { ...map };
          list.forEach((ib: any) => { mapAll[Number(ib.id)] = globalId!; });
          setInstalledHooks(mapAll);
        } else {
          setInstalledHooks(map);
        }
        setGlobalHookId(globalId);
      } catch (e) {
        console.warn('Falha ao listar webhooks do account', e);
      }
      toastUI(`Encontradas ${list.length} inbox(es) para a account ${accountId}`);
    } catch (e) {
      console.warn('Falha ao listar inboxes', e);
      toastUI(`Falha ao listar inboxes: ${String((e as any)?.message || e)}`);
    } finally {
      setInboxLoading(false);
    }
  };

  const installHook = async (inboxId: number) => {
    if (!cwToken || !cwAccountId) return;
    if (globalHookId || installedHooks[inboxId]) { toastUI('Já foi instalado'); return; }
    try {
      setHookLoadingId(inboxId);
      const body = { url: endpointUrl, inbox_id: inboxId, subscriptions: ['message_created', 'message_updated'] };
      const { data, error } = await supabase.functions.invoke('chatwoot-app-proxy', {
        body: { path: `/api/v1/accounts/${cwAccountId}/webhooks`, method: 'POST', token: cwToken, body },
      });
      if (error) {
        const msg = String((error as any)?.message || error);
        try {
          const sdk = getChatwootSDK();
          const hooksUpstream: any = await sdk.appProxy({ path: `/api/v1/accounts/${cwAccountId}/webhooks`, method: 'GET', token: cwToken });
          const hooksArr = extractArray(hooksUpstream);
          const existingSameInbox = hooksArr.find((h: any) => normalizeUrl(String(h?.url || '')) === normEndpoint && Number(h?.inbox_id ?? h?.inbox?.id ?? 0) === inboxId);
          const existingGlobal = hooksArr.find((h: any) => normalizeUrl(String(h?.url || '')) === normEndpoint && !(h?.inbox_id ?? h?.inbox?.id));
          if (existingSameInbox?.id) {
            setInstalledHooks((prev) => ({ ...prev, [inboxId]: Number(existingSameInbox.id) }));
            toastUI('Já foi instalado para este WhatsApp');
          } else if (existingGlobal?.id) {
            setGlobalHookId(Number(existingGlobal.id));
            toastUI('Já foi instalado para toda a conta');
          } else {
            toastUI(`Falha ao instalar: ${msg}`);
          }
        } catch (err) {
          toastUI(`Falha ao instalar: ${msg}`);
        }
      } else {
        const hook = (data as any)?.data;
        const id = Number(hook?.id || (Array.isArray(hook) ? hook[0]?.id : 0));
        if (id) {
          setInstalledHooks((prev) => ({ ...prev, [inboxId]: id }));
          toastUI('Webhook instalado');
        } else {
          try {
            const sdk = getChatwootSDK();
            const hooksUpstream: any = await sdk.appProxy({ path: `/api/v1/accounts/${cwAccountId}/webhooks`, method: 'GET', token: cwToken });
            const hooksArr = extractArray(hooksUpstream);
            const existingSameInbox = hooksArr.find((h: any) => normalizeUrl(String(h?.url || '')) === normEndpoint && Number(h?.inbox_id ?? h?.inbox?.id ?? 0) === inboxId);
            const existingGlobal = hooksArr.find((h: any) => normalizeUrl(String(h?.url || '')) === normEndpoint && !(h?.inbox_id ?? h?.inbox?.id));
            if (existingSameInbox?.id) {
              setInstalledHooks((prev) => ({ ...prev, [inboxId]: Number(existingSameInbox.id) }));
              toastUI('Webhook instalado');
            } else if (existingGlobal?.id) {
              const gid = Number(existingGlobal.id);
              setGlobalHookId(gid);
              // reflete imediatamente no mapa de inboxes visíveis
              setInstalledHooks((prev) => {
                const next = { ...prev };
                inboxes.forEach((ib: any) => { next[Number(ib.id)] = gid; });
                return next;
              });
              toastUI('Webhook instalado para toda a conta');
            } else {
              toastUI('Instalado, mas sem ID retornado');
            }
          } catch {
            toastUI('Instalado, mas sem ID retornado');
          }
        }
      }
    } catch (e: any) {
      const msg = String(e?.message || e);
      try {
        const sdk = getChatwootSDK();
        const hooksUpstream: any = await sdk.appProxy({ path: `/api/v1/accounts/${cwAccountId}/webhooks`, method: 'GET', token: cwToken });
        const hooksArr = extractArray(hooksUpstream);
        const existingSameInbox = hooksArr.find((h: any) => normalizeUrl(String(h?.url || '')) === normEndpoint && Number(h?.inbox_id ?? h?.inbox?.id ?? 0) === inboxId);
        const existingGlobal = hooksArr.find((h: any) => normalizeUrl(String(h?.url || '')) === normEndpoint && !(h?.inbox_id ?? h?.inbox?.id));
        if (existingSameInbox?.id) {
          setInstalledHooks((prev) => ({ ...prev, [inboxId]: Number(existingSameInbox.id) }));
          toastUI('Já foi instalado para este WhatsApp');
        } else if (existingGlobal?.id) {
          setGlobalHookId(Number(existingGlobal.id));
          toastUI('Já foi instalado para toda a conta');
        } else {
          toastUI(`Falha ao instalar: ${msg}`);
        }
      } catch (err) {
        toastUI(`Falha ao instalar: ${msg}`);
      }
    } finally {
      setHookLoadingId(null);
    }
  };

  const uninstallHook = async (inboxId: number) => {
    if (!cwToken || !cwAccountId) return;
    const hookId = installedHooks[inboxId] ?? globalHookId ?? null;
    if (!hookId) return;
    try {
      setHookLoadingId(inboxId);
      const sdk = getChatwootSDK();
      await sdk.appProxy({ path: `/api/v1/accounts/${cwAccountId}/webhooks/${hookId}`, method: 'DELETE', token: cwToken });
      setInstalledHooks((prev) => { const n = { ...prev }; delete n[inboxId]; return n; });
      if (globalHookId === hookId) setGlobalHookId(null);
      toastUI('Webhook removido');
    } catch (e) {
      toastUI(`Falha ao remover: ${String((e as any)?.message || e)}`);
    } finally { setHookLoadingId(null); }
  };

  useEffect(() => {
    const load = async () => {
      const { data: defs } = await supabase.from("ia_agent_defaults" as any).select("*").eq("status", "ativo");
      setDefaults((defs as any[]) || []);
      if (isAdminSaas && !tenantId) {
        const { data: tenants } = await supabase.from("tenants" as any).select("id,name");
        setTenantSelectList((tenants as any[]) || []);
      }
      if (activeTenantId) {
        const { data } = await supabase.from("ia_agents" as any).select("*").eq("tenant_id", activeTenantId);
        setItems((data as any[]) || []);
        const { data: kbs } = await supabase.from("knowledge_bases" as any).select("id,nome,descricao").eq("tenant_id", activeTenantId).eq("status", "ativo");
        setKbList((kbs as any[]) || []);
      }
    };
    load();
  }, [activeTenantId, isAdminSaas, tenantId]);

  useEffect(() => {
    const o: any = (form as any)?.overrides;
    if (Array.isArray(o)) {
      setSelectedKbIds(o.filter((x: any) => typeof x === 'string'));
    } else if (o && typeof o === 'object' && Array.isArray(o.kb_ids)) {
      setSelectedKbIds(o.kb_ids as string[]);
    } else if (!editing) {
      setSelectedKbIds([]);
    }
  }, [editing, form]);

  const prefillFromDefault = (slug: string) => {
    const d = defaults.find((x) => x.slug === slug);
    if (!d) return;
    setForm({
      ...form,
      default_id: d.id,
      default_version: d.version,
      nome: d.nome,
      system_prompt: d.system_prompt || "",
      activation_condition_prompt: d.activation_condition_prompt || "",
      base_agent_type: d.base_agent_type || "assistant",
      model: d.model || "auto",
      temperature: d.temperature ?? 0.7,
      max_tokens: d.max_tokens ?? 1024,
      status: "ativo",
    });
  };

  const save = async () => {
    if (!activeTenantId) return;
    const payload = {
      tenant_id: activeTenantId,
      default_id: form.default_id,
      default_version: form.default_version,
      slug: editing ? editing.slug : slugify(form.nome || ""),
      nome: form.nome,
      system_prompt: form.system_prompt,
      activation_condition_prompt: form.activation_condition_prompt,
      prompt_finishing: (form as any).prompt_finishing ?? null,
      base_agent_type: form.base_agent_type,
      model: null,
      temperature: null,
      max_tokens: null,
      status: form.status,
      overrides: { ...(form.overrides || {}), kb_ids: selectedKbIds },
      deployment_stage: form.deployment_stage,
      invoke_limit: form.invoke_limit,
      invoke_used: form.invoke_used ?? 0,
      parent_agent_id: form.parent_agent_id ?? null,
      is_current: form.is_current ?? false,
    } as any;
    if (editing) {
      const { error } = await supabase.from("ia_agents" as any).update(payload).eq("id", editing.id);
      if (error) {
        toastUI("Erro ao atualizar");
        return;
      }
    } else {
      const { error } = await supabase.from("ia_agents" as any).insert(payload);
      if (error) {
        toastUI("Erro ao criar");
        return;
      }
    }
    const { data } = await supabase.from("ia_agents" as any).select("*").eq("tenant_id", activeTenantId);
    setItems((data as any[]) || []);
    setOpen(false);
    setEditing(null);
    setForm({ status: "ativo", deployment_stage: "teste", version: 1, is_current: false });
  };

  const getNextVersion = async (base: Agent, tenant: string) => {
    const parent = base.parent_agent_id ?? base.id;
    const { data } = await supabase
      .from("ia_agents" as any)
      .select("id,version,slug,parent_agent_id")
      .eq("tenant_id", tenant)
      .or(`id.eq.${parent},parent_agent_id.eq.${parent}`) as any;
    const versions = ((data as any[]) || []).map((a: any) => a.version || 1);
    const maxV = versions.length ? Math.max(...versions) : 1;
    return maxV + 1;
  };

  const baseSlug = (s: string) => s.replace(/-v\d+$/i, "");

  const createVersion = async (base: Agent) => {
    if (!activeTenantId) return;
    const nextV = await getNextVersion(base, activeTenantId);
    const newSlug = `${baseSlug(base.slug)}-v${nextV}`;
    const payload = {
      tenant_id: activeTenantId,
      default_id: base.default_id,
      default_version: base.default_version,
      slug: newSlug,
      nome: base.nome,
      system_prompt: base.system_prompt,
      activation_condition_prompt: base.activation_condition_prompt,
      base_agent_type: base.base_agent_type,
      model: null,
      temperature: null,
      max_tokens: null,
      status: base.status,
      overrides: base.overrides ?? [],
      deployment_stage: "teste",
      invoke_limit: base.invoke_limit,
      invoke_used: 0,
      parent_agent_id: base.parent_agent_id ?? base.id,
      version: nextV,
      is_current: false,
    } as any;
    const { error } = await supabase.from("ia_agents" as any).insert(payload);
    if (error) {
      toastUI("Erro ao criar nova versão");
      return;
    }
    const { data } = await supabase.from("ia_agents" as any).select("*").eq("tenant_id", activeTenantId);
    setItems((data as any[]) || []);
    toastUI.success(`Nova versão criada: v${nextV}`);
  };

  const prepareNewVersion = async (base: Agent) => {
    if (!activeTenantId) return;
    const nextV = await getNextVersion(base, activeTenantId);
    const newSlug = `${baseSlug(base.slug)}-v${nextV}`;
    setForm({
      tenant_id: activeTenantId,
      default_id: base.default_id,
      default_version: base.default_version,
      slug: newSlug,
      nome: base.nome,
      system_prompt: base.system_prompt,
      activation_condition_prompt: base.activation_condition_prompt,
      prompt_finishing: base.prompt_finishing ?? null,
      base_agent_type: base.base_agent_type,
      model: null,
      temperature: null,
      max_tokens: null,
      status: base.status,
      overrides: base.overrides ?? [],
      deployment_stage: "teste",
      invoke_limit: 3,
      invoke_used: 0,
      parent_agent_id: base.parent_agent_id ?? base.id,
      version: nextV,
      is_current: false,
    } as any);
    setEditing(null);
  };

  const baseTypeLabel = (v?: string | null) => {
    if (v === "assistant") return "Assistente";
    if (v === "router") return "Roteador";
    if (v === "tool_executor") return "Executor de Ferramentas";
    return v || "";
  };

  const stageLabel = (s?: string | null) => {
    if (s === "producao") return "Ativo";
    if (s === "teste") return "Testando";
    return s || "";
  };

  const versionsByParent = useMemo(() => {
    const map: Record<string, Agent[]> = {};
    items.forEach((a) => {
      if (a.parent_agent_id) {
        const pid = a.parent_agent_id as string;
        if (!map[pid]) map[pid] = [];
        map[pid].push(a);
      }
    });
    return map;
  }, [items]);

  const rootAgents = useMemo(() => items.filter((a) => !a.parent_agent_id), [items]);
  const filteredRootAgents = useMemo(() => {
    const base = rootAgents;
    if (!searchTerm) return base;
    const s = searchTerm.toLowerCase();
    return base.filter((a) => (a.nome || "").toLowerCase().includes(s) || (a.slug || "").toLowerCase().includes(s));
  }, [rootAgents, searchTerm]);
  const paginatedRootAgents = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRootAgents.slice(start, start + itemsPerPage);
  }, [filteredRootAgents, currentPage, itemsPerPage]);
  const activeFilterChips = useMemo<{ id: string; label: React.ReactNode; onClear: () => void }[]>(() => {
    const chips: { id: string; label: React.ReactNode; onClear: () => void }[] = [];
    if (searchTerm) {
      const term = searchTerm;
      chips.push({ id: `search:${term}`, label: `Busca: "${term}"`, onClear: () => { setCurrentPage(1); setSearchTerm(""); } });
    }
    return chips;
  }, [searchTerm]);

  if (!activeTenantId && isAdminSaas) {
    return (
      <div className="p-6 space-y-4">
        <div className="text-xl font-semibold">Agentes de IA</div>
        <div className="max-w-md">
          <label className="text-sm">Selecione um tenant</label>
          <Select value={selectedTenantId || ""} onValueChange={(v) => setSelectedTenantId(v)}>
            <SelectTrigger>
              <SelectValue placeholder="Escolher" />
            </SelectTrigger>
            <SelectContent>
              {tenantSelectList.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-cy="iagents-page">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold" data-cy="iagents-title">Minhas IAs</h1>
          <p className="text-muted-foreground">Gerencie seus agentes de IA</p>
        </div>
        <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center md:justify-end md:gap-3">
          <div className="flex w-full flex-row flex-wrap gap-2 md:w-auto">
            {hasToken ? (
              <Button variant="outline" className="flex-1 min-w-[150px] md:w-auto" onClick={openInboxDialog}>Instalar IA</Button>
            ) : (
              <Button variant="outline" className="flex-1 min-w-[150px] md:w-auto" onClick={() => navigate('/configuracoes/chatwoot?tab=setup&sub=agents')}>Integrar Chatwoot</Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="flex-1 min-w-[150px] md:w-auto" onClick={() => { setEditing(null); setForm({ status: "ativo", deployment_stage: "teste", is_current: false }); setSelectedKbIds([]); }}>Novo Agente</Button>
              </DialogTrigger>
          <DialogContent className="sm:max-w-6xl">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar Agente" : "Criar Agente"}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                <div>
                  <label className="text-sm">IA Pré-Configurada</label>
                  <Select value={form.default_slug || ""} onValueChange={(v) => { setForm({ ...form, default_slug: v }); prefillFromDefault(v); }}>
                    <SelectTrigger><SelectValue placeholder="Escolher IA pronta para uso" /></SelectTrigger>
                    <SelectContent>
                      {defaults.map((d) => (<SelectItem key={d.id} value={d.slug}>{d.nome}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm">Nome</label>
                  <Input placeholder="Nome que identifique esta IA" value={form.nome || ""} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                  {form.nome ? (
                    <p className="text-xs text-muted-foreground">Slug: <code className="font-mono">{slugify(form.nome)}</code></p>
                  ) : null}
                </div>
                <div>
                  <label className="text-sm">Limite de Testes</label>
                  <Input placeholder="Quantidade de execuções de teste" type="number" value={form.invoke_limit?.toString() || ""} onChange={(e) => setForm({ ...form, invoke_limit: Number(e.target.value) })} />
                </div>
                <div>
                  <label className="text-sm">Status</label>
                  <Select value={form.status || "ativo"} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ativo">Ativo</SelectItem>
                      <SelectItem value="inativo">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm">Estágio</label>
                  <Select value={form.deployment_stage || "teste"} onValueChange={(v) => setForm({ ...form, deployment_stage: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecione o estágio" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="teste">Testando</SelectItem>
                      <SelectItem value="producao">Ativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Tabs defaultValue="system">
                <TabsList className="mb-2">
                  <TabsTrigger value="system" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">Comportamento</TabsTrigger>
                  <TabsTrigger value="activation" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">Ativação</TabsTrigger>
                  <TabsTrigger value="finishing" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">Finalização</TabsTrigger>
                  <TabsTrigger value="trainings" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">Treinamentos</TabsTrigger>
                </TabsList>
                <TabsContent value="system">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Defina o comportamento padrão da IA. Use tópicos e exemplos claros.</p>
                    <div className="rounded border mdx-container">
                      <MDXEditor
                        markdown={form.system_prompt || ""}
                        onChange={(v) => setForm({ ...form, system_prompt: v })}
                      plugins={[
                        headingsPlugin({ allowedHeadingLevels: [1,2,3] }),
                        listsPlugin(),
                        markdownShortcutPlugin(),
                        quotePlugin(),
                        linkPlugin(),
                        linkDialogPlugin(),
                        tablePlugin(),
                        directivesPlugin({ directiveDescriptors: [AdmonitionDirectiveDescriptor] }),
                        codeBlockPlugin({ defaultCodeBlockLanguage: "txt" }),
                        thematicBreakPlugin(),
                        toolbarPlugin({
                           toolbarContents: () => (
                            <div className="flex items-center gap-2">
                              <BlockTypeSelect />
                              <BoldItalicUnderlineToggles />
                              <CodeToggle />
                              <ListsToggle />
                              <CreateLink />
                              <InsertTable />
                              <InsertAdmonition />
                              <InsertThematicBreak />
                              <Separator />
                              <MDXToolbarButton onClick={() => setFullscreenEditor({ title: "Comportamento", value: form.system_prompt || "", onChange: (v) => setForm({ ...form, system_prompt: v }) })}>
                                <Maximize2 className="h-4 w-4" />
                              </MDXToolbarButton>
                            </div>
                          ),
                        }),
                      ]}
                      className="min-h-[240px]"
                    />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="finishing">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Especifique como concluir conversas e as regras de encerramento de atendimento.</p>
                    <div className="rounded border mdx-container">
                      <MDXEditor
                        markdown={(form as any).prompt_finishing || ""}
                        onChange={(v) => setForm({ ...form, prompt_finishing: v } as any)}
                      plugins={[
                        headingsPlugin({ allowedHeadingLevels: [1,2,3] }),
                        listsPlugin(),
                        markdownShortcutPlugin(),
                        quotePlugin(),
                        linkPlugin(),
                        linkDialogPlugin(),
                        tablePlugin(),
                        directivesPlugin({ directiveDescriptors: [AdmonitionDirectiveDescriptor] }),
                        codeBlockPlugin({ defaultCodeBlockLanguage: "txt" }),
                        thematicBreakPlugin(),
                        toolbarPlugin({
                          toolbarContents: () => (
                            <div className="flex items-center gap-2">
                              <BlockTypeSelect />
                              <BoldItalicUnderlineToggles />
                              <CodeToggle />
                              <ListsToggle />
                              <CreateLink />
                              <InsertTable />
                              <InsertAdmonition />
                              <InsertThematicBreak />
                              <Separator />
                              <MDXToolbarButton onClick={() => setFullscreenEditor({ title: "Finalização", value: (form as any).prompt_finishing || "", onChange: (v) => setForm({ ...form, prompt_finishing: v } as any) })}>
                                <Maximize2 className="h-4 w-4" />
                              </MDXToolbarButton>
                            </div>
                          ),
                        }),
                      ]}
                      className="min-h-[200px]"
                    />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="activation">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Descreva as condições de ativação e critérios para a IA intervir.</p>
                    <div className="rounded border mdx-container">
                      <MDXEditor
                        markdown={form.activation_condition_prompt || ""}
                        onChange={(v) => setForm({ ...form, activation_condition_prompt: v })}
                      plugins={[
                        headingsPlugin({ allowedHeadingLevels: [1,2,3] }),
                        listsPlugin(),
                        markdownShortcutPlugin(),
                        quotePlugin(),
                        linkPlugin(),
                        linkDialogPlugin(),
                        tablePlugin(),
                        directivesPlugin({ directiveDescriptors: [AdmonitionDirectiveDescriptor] }),
                        codeBlockPlugin({ defaultCodeBlockLanguage: "txt" }),
                        thematicBreakPlugin(),
                        toolbarPlugin({
                          toolbarContents: () => (
                            <div className="flex items-center gap-2">
                              <BlockTypeSelect />
                              <BoldItalicUnderlineToggles />
                              <CodeToggle />
                              <ListsToggle />
                              <CreateLink />
                              <InsertTable />
                              <InsertAdmonition />
                              <InsertThematicBreak />
                              <Separator />
                              <MDXToolbarButton onClick={() => setFullscreenEditor({ title: "Ativação", value: form.activation_condition_prompt || "", onChange: (v) => setForm({ ...form, activation_condition_prompt: v }) })}>
                                <Maximize2 className="h-4 w-4" />
                              </MDXToolbarButton>
                            </div>
                          ),
                        }),
                      ]}
                      className="min-h-[200px]"
                    />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="trainings">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Selecione as bases de conhecimento para relacionar com este agente.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {kbList.length === 0 ? (
                        <div className="text-sm text-muted-foreground">Nenhuma base de conhecimento ativa encontrada.</div>
                      ) : (
                        kbList.map((kb) => {
                          const id = `kb-${kb.id}`;
                          const checked = selectedKbIds.includes(kb.id);
                          return (
                            <div key={kb.id} className="flex items-start space-x-2 rounded border p-2">
                              <Checkbox id={id} className="ml-[3px] mt-[3px]" checked={checked} onCheckedChange={(c) => {
                                const v = !!c;
                                setSelectedKbIds((prev) => v ? [...prev, kb.id] : prev.filter((x) => x !== kb.id));
                              }} />
                              <div className="flex-1">
                                <Label htmlFor={id} className="cursor-pointer">{kb.nome}</Label>
                                {kb.descricao ? (<p className="text-xs text-muted-foreground mt-0.5">{kb.descricao}</p>) : null}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
              <div className="flex justify-end items-center mt-2 gap-2">
                <Button variant="outline" onClick={() => { setOpen(false); setEditing(null); setForm({ status: "ativo", deployment_stage: "teste", version: 1, is_current: false }); setSelectedKbIds([]); }}>Cancelar</Button>
                <Button onClick={save}>Salvar</Button>
              </div>
            </div>
          </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <Card data-cy="iagents-table-card">
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle data-cy="iagents-table-title">Lista de IAs</CardTitle>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative w-full sm:w-[200px]">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="pl-8"
                    data-cy="iagents-search-input"
                  />
                </div>
              </div>
            </div>
            {activeFilterChips.length > 0 && (
              <div className="text-[13px] md:text-sm text-muted-foreground">
                <span className="mr-2 font-bold">Filtros ativos:</span>
                <div className="inline-flex flex-wrap gap-0.5">
                  {activeFilterChips.map((chip) => (
                    <Badge key={chip.id} variant="secondary" className="h-5 px-1.5 py-0 inline-flex items-center gap-1 border border-gray-300">
                      <span>{chip.label}</span>
                      <button
                        type="button"
                        onClick={chip.onClear}
                        className="text-red-600 hover:bg-red-600 hover:text-white rounded-full w-4 h-4 inline-flex items-center justify-center leading-none"
                        aria-label={`Remover ${chip.label}`}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Versão</TableHead>
                  <TableHead>Estágio</TableHead>
                  <TableHead className="text-center">Limite</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedRootAgents.map((root) => {
                  const group = [root, ...(versionsByParent[root.id] || [])].sort((a,b)=> (a.version||1)-(b.version||1));
                  const current = group.find((a)=> a.is_current) || root;
                  const versions = group.filter((a)=> a.id !== current.id);
                  return (
                  <Fragment key={root.id}>
                    <TableRow
                      key={`current-${root.id}`}
                      className={expanded[root.id] ? "bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-none [&>td]:py-1 [&>td]:px-2" : "[&>td]:py-1 [&>td]:px-2"}
                    >
                      <TableCell className={expanded[root.id] ? "rounded-tl-md" : undefined}>{current.nome}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-zinc-300 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-100 border-transparent">Versão {current.version}</Badge>
                      </TableCell>
                      <TableCell>{stageLabel(current.deployment_stage)}</TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant="secondary"
                          className="cursor-pointer text-xs px-2 py-0.5 bg-zinc-300 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-100 border-transparent"
                          onClick={() => {
                            setLimitEditTarget(current);
                            setLimitUnlimited(current.invoke_limit == null);
                            setLimitEditValue(current.invoke_limit != null ? String(current.invoke_limit) : "");
                            setLimitEditOpen(true);
                          }}
                        >
                          {current.invoke_limit != null ? `${current.invoke_used}/${current.invoke_limit}` : '—'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">{current.status}</TableCell>
                      <TableCell className={expanded[root.id] ? "rounded-tr-md text-right" : "text-right"}>
                        <div className="flex gap-1 justify-end">
                          <Button variant="outline" size="sm" onClick={() => setExpanded((e) => ({ ...e, [root.id]: !e[root.id] }))}>{expanded[root.id] ? "Fechar" : "Versões"}</Button>
                          {!current.is_current && (
                            <Button variant="outline" size="sm" onClick={() => { setActivateTarget(current); setActivateMode('definitivo'); setConfirmActivateOpen(true); }}>Ativar</Button>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="outline" size="icon" onClick={() => { setVersionBase(current); setConfirmVersionOpen(true); }}>
                                <GitBranch className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-xs"><div className="font-bold">{current.nome}</div><div>Nova Versão</div></div>
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="sm" className="icon-chip-green" onClick={() => { setEditing(current); setForm(current); setOpen(true); }}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-xs"><div className="font-bold">{current.nome}</div><div>Editar</div></div>
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="sm" className="icon-chip-red" onClick={() => { setDeleteTarget(current); setConfirmDeleteOpen(true); }}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-xs"><div className="font-bold">{current.nome}</div><div>Excluir</div></div>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expanded[root.id] && versions.map((v, idx) => (
                      <TableRow
                        key={v.id}
                        className="bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-none [&>td]:py-1 [&>td]:px-2"
                      >
                        <TableCell className={idx === versions.length - 1 ? "rounded-bl-md" : undefined}>{v.nome}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-zinc-300 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-100 border-transparent">Versão {v.version}</Badge>
                        </TableCell>
                        <TableCell>{stageLabel(v.deployment_stage)}</TableCell>
                        <TableCell className="text-center">
                        <Badge
                          variant="secondary"
                          className="cursor-pointer text-xs px-2 py-0.5 bg-zinc-300 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-100 border-transparent"
                          onClick={() => {
                              setLimitEditTarget(v);
                              setLimitUnlimited(v.invoke_limit == null);
                              setLimitEditValue(v.invoke_limit != null ? String(v.invoke_limit) : "");
                              setLimitEditOpen(true);
                            }}
                          >
                            {v.invoke_limit != null ? `${v.invoke_used}/${v.invoke_limit}` : '—'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">{v.status}</TableCell>
                        <TableCell className={idx === versions.length - 1 ? "rounded-br-md text-right" : "text-right"}>
                          <div className="flex gap-1 justify-end">
                            {!v.is_current && (
                              <Button variant="outline" size="sm" onClick={() => { setActivateTarget(v); setActivateMode('definitivo'); setConfirmActivateOpen(true); }}>Ativar</Button>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="outline" size="icon" onClick={() => { setVersionBase(v); setConfirmVersionOpen(true); }}>
                                  <GitBranch className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-xs"><div className="font-bold">{v.nome}</div><div>Nova Versão</div></div>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="icon-chip-green" onClick={() => { setEditing(v); setForm(v); setOpen(true); }}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-xs"><div className="font-bold">{v.nome}</div><div>Editar</div></div>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="icon-chip-red" onClick={() => { setDeleteTarget(v); setConfirmDeleteOpen(true); }}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-xs"><div className="font-bold">{v.nome}</div><div>Excluir</div></div>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <TablePagination
            currentPage={currentPage}
            totalPages={Math.ceil(filteredRootAgents.length / itemsPerPage) || 1}
            totalItems={filteredRootAgents.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(value) => { setItemsPerPage(value); setCurrentPage(1); }}
            data-cy="iagents-pagination"
          />
        </CardContent>
      </Card>

      {fullscreenEditor && (
        <Dialog open onOpenChange={(open) => { if (!open) setFullscreenEditor(null); }}>
          <DialogContent className="sm:max-w-[95vw] w-[95vw] h-[90vh]">
            <DialogHeader>
              <DialogTitle>{fullscreenEditor.title}</DialogTitle>
            </DialogHeader>
            <div className="rounded border mdx-container h-full">
              <MDXEditor
                markdown={fullscreenEditor.value || ""}
                onChange={(v) => setFullscreenEditor({ ...fullscreenEditor, value: v })}
                plugins={[
                  headingsPlugin({ allowedHeadingLevels: [1,2,3] }),
                  listsPlugin(),
                  markdownShortcutPlugin(),
                  quotePlugin(),
                  linkPlugin(),
                  linkDialogPlugin(),
                  tablePlugin(),
                  directivesPlugin({ directiveDescriptors: [AdmonitionDirectiveDescriptor] }),
                  codeBlockPlugin({ defaultCodeBlockLanguage: "txt" }),
                  thematicBreakPlugin(),
                  toolbarPlugin({
                    toolbarContents: () => (
                      <div className="flex items-center gap-2">
                        <BlockTypeSelect />
                        <BoldItalicUnderlineToggles />
                        <CodeToggle />
                        <ListsToggle />
                        <CreateLink />
                        <InsertTable />
                        <InsertAdmonition />
                        <InsertThematicBreak />
                      </div>
                    ),
                  }),
                ]}
                className="h-[70vh]"
              />
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => { fullscreenEditor.onChange(fullscreenEditor.value); setFullscreenEditor(null); }}>Fechar</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={inboxOpen} onOpenChange={setInboxOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Lista de WhatsApp</DialogTitle>
            <DialogDescription>
              Selecione quais WhatsApp você deseja instalar IAs. A instalação prepara o WhatsApp para enviar eventos via webhook, permitindo atribuir uma IA posteriormente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {inboxLoading ? (
              <div className="text-sm text-muted-foreground">Carregando...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inboxes.map((ib) => (
                    <TableRow key={ib.id}>
                      <TableCell>{ib.id}</TableCell>
                      <TableCell>{ib.name}</TableCell>
                      <TableCell className="text-right">
                        {(installedHooks[ib.id] || globalHookId) ? (
                          <Button variant="destructive" onClick={() => uninstallHook(ib.id)} disabled={hookLoadingId === ib.id}>
                            {hookLoadingId === ib.id ? (<span className="inline-flex items-center gap-2"><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/></svg>Desinstalando</span>) : (<span className="inline-flex items-center gap-2"><Trash2 className="h-4 w-4" />Desinstalar</span>)}
                          </Button>
                        ) : (
                          <Button onClick={() => installHook(ib.id)} disabled={hookLoadingId === ib.id}>
                            {hookLoadingId === ib.id ? (<span className="inline-flex items-center gap-2"><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/></svg>Instalando</span>) : (<span className="inline-flex items-center gap-2"><Link2 className="h-4 w-4" />Instalar</span>)}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      

      

      <Dialog open={confirmVersionOpen} onOpenChange={setConfirmVersionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar nova versão</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Confirmar criação de uma nova versão para este agente?</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmVersionOpen(false)}>Cancelar</Button>
              <Button
                onClick={async () => {
                  if (versionBase) {
                    if (prepareVersionOnly) {
                      await prepareNewVersion(versionBase);
                    } else {
                      await createVersion(versionBase);
                    }
                  }
                  setConfirmVersionOpen(false);
                  setVersionBase(null);
                  setPrepareVersionOnly(false);
                }}
              >Confirmar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir agente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Confirma a exclusão deste agente/versão? Esta ação não pode ser desfeita.</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)}>Cancelar</Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  if (!deleteTarget || !activeTenantId) return;
                  const { error } = await supabase
                    .from('ia_agents' as any)
                    .delete()
                    .eq('id', deleteTarget.id)
                    .eq('tenant_id', activeTenantId);
                  if (error) {
                    toastUI('Erro ao excluir');
                  } else {
                    const { data } = await supabase.from('ia_agents' as any).select('*').eq('tenant_id', activeTenantId);
                    setItems((data as any[]) || []);
                    setConfirmDeleteOpen(false);
                    setDeleteTarget(null);
                    toastUI.success('Excluído');
                  }
                }}
              >Excluir</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={limitEditOpen} onOpenChange={setLimitEditOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Alterar limite</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">{limitEditTarget?.nome}</div>
            <div className="grid grid-cols-1 gap-2">
              <label className="text-sm">Novo limite (execuções)</label>
              <Input
                type="number"
                placeholder="Informe um número"
                value={limitUnlimited ? "" : limitEditValue}
                disabled={limitUnlimited}
                onChange={(e) => setLimitEditValue(e.target.value)}
              />
              <div className="flex gap-2">
                <Button variant={limitUnlimited ? 'default' : 'outline'} size="sm" onClick={() => setLimitUnlimited(!limitUnlimited)}>Sem limite</Button>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setLimitEditOpen(false)}>Cancelar</Button>
              <Button
                onClick={async () => {
                  if (!limitEditTarget || !activeTenantId) return;
                  const nextLimit = limitUnlimited ? null : Number(limitEditValue || 0);
                  const { error } = await supabase
                    .from('ia_agents' as any)
                    .update({ invoke_limit: nextLimit })
                    .eq('id', limitEditTarget.id)
                    .eq('tenant_id', activeTenantId);
                  if (error) {
                    toastUI('Erro ao atualizar limite');
                  } else {
                    const { data } = await supabase.from('ia_agents' as any).select('*').eq('tenant_id', activeTenantId);
                    setItems((data as any[]) || []);
                    setLimitEditOpen(false);
                    setLimitEditTarget(null);
                    toastUI.success('Limite atualizado');
                  }
                }}
              >Salvar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmActivateOpen} onOpenChange={setConfirmActivateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ativar versão</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Button variant={activateMode==='definitivo'?'default':'outline'} onClick={()=>setActivateMode('definitivo')}>Ativar definitivamente</Button>
              <Button variant={activateMode==='limitado'?'default':'outline'} onClick={()=>setActivateMode('limitado')}>Ativar por execuções</Button>
            </div>
            {activateMode==='limitado' && (
              <div>
                <label className="text-sm">Quantidade de execuções</label>
                <Input type="number" value={String(activateLimit)} onChange={(e)=>setActivateLimit(Number(e.target.value)||0)} />
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={()=>setConfirmActivateOpen(false)}>Cancelar</Button>
              <Button onClick={async ()=>{
                if (!activateTarget || !activeTenantId) return;
                const parent = activateTarget.parent_agent_id ?? activateTarget.id;
                await supabase.from('ia_agents' as any)
                  .update({ is_current: false })
                  .eq('tenant_id', activeTenantId)
                  .or(`id.eq.${parent},parent_agent_id.eq.${parent}`);
                const upd = activateMode==='definitivo'
                  ? { is_current: true, deployment_stage: 'producao', invoke_limit: null, invoke_used: 0 }
                  : { is_current: true, deployment_stage: 'producao', invoke_limit: activateLimit, invoke_used: 0 };
                await supabase.from('ia_agents' as any)
                  .update(upd as any)
                  .eq('id', activateTarget.id)
                  .eq('tenant_id', activeTenantId);
                const { data } = await supabase.from('ia_agents' as any).select('*').eq('tenant_id', activeTenantId);
                setItems((data as any[])||[]);
                setConfirmActivateOpen(false);
                setActivateTarget(null);
                toastUI.success('Versão ativada');
              }}>Confirmar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
